import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { X, Plus } from 'lucide-react'
import { fetchMethodologies } from '../api/generationApi'

const SERVICE_LINE_GROUPS = [
  {
    label: 'CRM & ERP PLATFORMS',
    color: 'text-purple-600',
    items: [
      { id: 'salesforce', label: 'Salesforce' },
      { id: 'netsuite', label: 'NetSuite' },
      { id: 'sap', label: 'SAP' },
      { id: 'oracle', label: 'Oracle Applications' },
    ],
  },
  {
    label: 'ITSM & WORKFLOW',
    color: 'text-green-600',
    items: [{ id: 'servicenow', label: 'ServiceNow' }],
  },
  {
    label: 'CUSTOM DEVELOPMENT',
    color: 'text-blue-600',
    items: [
      { id: 'dotnet', label: '.NET' },
      { id: 'python', label: 'Python' },
      { id: 'java', label: 'Java' },
    ],
  },
  {
    label: 'FRONTEND FRAMEWORKS',
    color: 'text-orange-500',
    items: [
      { id: 'react', label: 'React' },
      { id: 'angular', label: 'Angular' },
    ],
  },
  {
    label: 'AI & INTELLIGENCE',
    color: 'text-red-500',
    items: [
      { id: 'agentic_ai', label: 'Agentic AI' },
      { id: 'ai_ml', label: 'AI/ML' },
      { id: 'data_ai', label: 'Data & AI' },
    ],
  },
  {
    label: 'CLOUD PLATFORMS',
    color: 'text-sky-500',
    items: [
      { id: 'azure', label: 'Azure' },
      { id: 'aws', label: 'AWS' },
      { id: 'gcp', label: 'GCP' },
    ],
  },
]

const PROJECT_TYPES = [
  { value: 'ams',            label: 'AMS / RUN Services',        hint: 'Managed services, support, SLA-based engagement' },
  { value: 'implementation', label: 'Platform Implementation',   hint: 'Salesforce / ServiceNow implementation or rollout' },
  { value: 'integration',    label: 'Integration / Middleware',  hint: 'Connecting existing systems via APIs or middleware' },
  { value: 'custom_dev',     label: 'Custom Development',        hint: 'Building new software or applications' },
  { value: 'data_analytics', label: 'Data & Analytics',          hint: 'Data pipelines, dashboards, warehouses' },
]

// Only what the engagement cannot supply.
//
// Dropped from this form deliberately:
//   client_name       inherited from the engagement, and set server-side by
//                     CreateProjectHandler so the two can never disagree
//   description       nothing has ever read it — not one prompt, not one export
//   engagement_model  nothing has ever read it either; the axis that actually
//                     shapes documents is contract_type, which lives on the
//                     engagement
//
// Jira config stays out too: it belongs in project settings, not in the
// create step, and pushing to Jira is not part of creating a project.
const empty = {
  name: '',
  methodology: '',
  project_type: '',
  service_line: [],
}

export default function NewProjectModal({ onClose, onSubmit, loading, engagement }) {
  const [form, setForm] = useState(empty)
  const [errors, setErrors] = useState({})
  const [methodologies, setMethodologies] = useState([])

  useEffect(() => {
    fetchMethodologies()
      .then(setMethodologies)
      .catch(() => {}) // dropdown stays empty on error; user can still type
  }, [])

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }))

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      service_line: f.service_line.includes(id)
        ? f.service_line.filter((s) => s !== id)
        : [...f.service_line, id],
    }))
  }

  const validate = () => {
    const e = {}
    if (!form.name.trim()) e.name = 'Draft name is required'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = () => {
    if (!validate()) return
    onSubmit({
      ...form,
      service_line: form.service_line.join(', '),
    })
  }

  // Read-only context, so it is obvious what the project inherits rather than
  // silently arriving on the created record.
  const inherited = [
    ['Project', engagement?.client_name],
    ['Contract type', engagement?.contract_type],
    ['Platform', engagement?.platform],
  ].filter(([, value]) => value)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Create New Draft</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 space-y-4 flex-1">
          {/* Project Name */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Draft Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g. CRM Modernization Phase 2"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.name ? 'border-red-400' : 'border-gray-200'
              }`}
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          {/* Inherited from the engagement — shown, never asked for */}
          {inherited.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
              <p className="text-[11px] font-medium text-gray-500 mb-1.5">
                Inherited from {engagement?.client_name ? 'this engagement' : 'the engagement'}
              </p>
              <dl className="grid grid-cols-3 gap-2">
                {inherited.map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-[10px] uppercase tracking-wide text-gray-400">{label}</dt>
                    <dd className="text-xs text-gray-800 truncate" title={value}>{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {/* Methodology */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Methodology</label>
              <select
                value={form.methodology}
                onChange={(e) => set('methodology', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-700 appearance-none"
                style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
              >
                <option value="">Select methodology</option>
                {methodologies.map((m) => (
                  <option key={m.id} value={m.code}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Project Type */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Project Type <span className="text-gray-400 font-normal">(helps AI generate accurate design)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {PROJECT_TYPES.map((pt) => {
                const active = form.project_type === pt.value
                return (
                  <button
                    key={pt.value}
                    type="button"
                    onClick={() => set('project_type', active ? '' : pt.value)}
                    title={pt.hint}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                      active
                        ? 'bg-blue-50 border-blue-400 text-blue-700 font-medium'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {pt.label}
                  </button>
                )
              })}
            </div>
            {form.project_type && (
              <p className="text-[11px] text-gray-400 mt-1.5">
                {PROJECT_TYPES.find((pt) => pt.value === form.project_type)?.hint}
              </p>
            )}
          </div>

          {/* Service Lines */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-3">
              Service Lines <span className="text-gray-400 font-normal">(select applicable)</span>
            </label>
            <div className="space-y-3">
              {SERVICE_LINE_GROUPS.map((group) => (
                <div key={group.label}>
                  <p className={`text-[10px] font-bold tracking-wider mb-1.5 ${group.color}`}>
                    {group.label}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {group.items.map((item) => {
                      const active = form.service_line.includes(item.id)
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => toggleService(item.id)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border transition-all ${
                            active
                              ? 'bg-blue-50 border-blue-400 text-blue-700 font-medium'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {item.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Jira configuration deliberately not here. It belongs in
              project settings: it is not needed to create a project, and
              pushing to Jira is a later step in the lifecycle. */}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
          >
            <Plus size={14} />
            {loading ? 'Creating...' : 'Create Draft'}
          </button>
        </div>
      </div>
    </div>
  )
}

NewProjectModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  // The engagement this project is created under. Optional so the modal still
  // renders while WorkspaceContext is loading — the inherited panel simply
  // does not appear until it resolves.
  engagement: PropTypes.shape({
    engagement_id: PropTypes.string,
    client_name: PropTypes.string,
    contract_type: PropTypes.string,
    platform: PropTypes.string,
  }),
}
